import type { Entry } from '@/types/domain'
import {
  CorruptedFileError,
  UnsupportedEnvelopeError,
  WrongPasswordError,
  decryptString,
} from '@/core/crypto/decrypt'
import { type CryptoOptions } from '@/core/crypto/encrypt'
import {
  exportPayloadEncryptedSchema,
  exportPayloadPlainSchema,
  exportPayloadSchema,
  type ExportPayloadPlain,
} from './schema'

export type MergeStrategy = 'replace-all' | 'merge-preserve' | 'merge-overwrite'

export type ImportErrorCode =
  | 'PARSE'
  | 'SCHEMA'
  | 'NEEDS_PASSWORD'
  | 'WRONG_PASSWORD'
  | 'UNSUPPORTED'
  | 'CORRUPTED'

export class ImportError extends Error {
  readonly code: ImportErrorCode

  constructor(code: ImportErrorCode, message: string) {
    super(message)
    this.name = 'ImportError'
    this.code = code
  }
}

export interface ImportPlan {
  payload: ExportPayloadPlain
  conflictingIds: string[]
  encrypted: boolean
}

export interface ParseImportInput {
  fileContent: string
  password?: string
  currentEntryIds?: string[]
  cryptoOptions?: CryptoOptions
}

/**
 * Parses an export file (plain or encrypted), validates the schema and
 * reports conflicts against the currently-known entry ids so the caller
 * can show the user which entries would be replaced.
 *
 * Never mutates application state. The caller decides on a MergeStrategy
 * and feeds the plan into `applyImport`.
 */
export async function parseImport(input: ParseImportInput): Promise<ImportPlan> {
  let raw: unknown
  try {
    raw = JSON.parse(input.fileContent)
  } catch (err) {
    throw new ImportError(
      'PARSE',
      `JSON inválido: ${err instanceof Error ? err.message : 'erro desconhecido'}`,
    )
  }

  const envelopeMatch = exportPayloadSchema.safeParse(raw)
  if (!envelopeMatch.success) {
    throw new ImportError('SCHEMA', formatZodMessage(envelopeMatch.error))
  }

  let plain: ExportPayloadPlain
  let encrypted = false
  if ('encrypted' in envelopeMatch.data && envelopeMatch.data.encrypted) {
    encrypted = true
    const envelope = exportPayloadEncryptedSchema.parse(envelopeMatch.data)
    if (!input.password) {
      throw new ImportError('NEEDS_PASSWORD', 'arquivo criptografado: forneça a senha')
    }
    let decrypted: string
    try {
      decrypted = await decryptString(envelope, input.password, input.cryptoOptions)
    } catch (err) {
      if (err instanceof WrongPasswordError) {
        throw new ImportError('WRONG_PASSWORD', 'senha incorreta')
      }
      if (err instanceof CorruptedFileError) {
        throw new ImportError('CORRUPTED', err.message)
      }
      if (err instanceof UnsupportedEnvelopeError) {
        throw new ImportError('UNSUPPORTED', err.message)
      }
      throw err
    }

    let plainRaw: unknown
    try {
      plainRaw = JSON.parse(decrypted)
    } catch {
      throw new ImportError('CORRUPTED', 'conteúdo decifrado não é JSON válido')
    }
    const plainParsed = exportPayloadPlainSchema.safeParse(plainRaw)
    if (!plainParsed.success) {
      throw new ImportError('SCHEMA', formatZodMessage(plainParsed.error))
    }
    plain = plainParsed.data
  } else {
    plain = exportPayloadPlainSchema.parse(envelopeMatch.data)
  }

  const currentIds = new Set(input.currentEntryIds ?? [])
  const conflictingIds = plain.entries
    .filter((e) => currentIds.has(e.id))
    .map((e) => e.id)

  return { payload: plain, conflictingIds, encrypted }
}

export interface ApplyImportInput {
  plan: ImportPlan
  strategy: MergeStrategy
  currentEntries: Entry[]
}

/**
 * Pure function: given the current entries and an import plan, returns
 * the next list of entries according to the chosen MergeStrategy.
 * Callers are responsible for writing the result back to the store.
 */
export function applyImport(input: ApplyImportInput): Entry[] {
  const incoming = input.plan.payload.entries as Entry[]
  if (input.strategy === 'replace-all') return incoming

  const map = new Map<string, Entry>(input.currentEntries.map((e) => [e.id, e]))
  for (const e of incoming) {
    const exists = map.has(e.id)
    if (input.strategy === 'merge-preserve' && exists) continue
    map.set(e.id, e)
  }
  return Array.from(map.values())
}

function formatZodMessage(err: { issues: { path: (string | number)[]; message: string }[] }): string {
  const first = err.issues[0]
  if (!first) return 'schema inválido'
  const path = first.path.length > 0 ? first.path.join('.') : '<root>'
  return `${path}: ${first.message}`
}
